import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import type { PhraseLibrary } from '@ast-data/shared';

interface PhrasePickerPanelProps {
    /** The phrases to browse — one library among possible others (the reuse point). */
    library: PhraseLibrary;
    /** Called with the tapped sentence; the caller splices it into its field. */
    onSelect: (phrase: string) => void;
}

/** A phrase as it appears in the flat search list — carries its category label. */
interface FlatPhrase {
    phrase: string;
    categoryLabel: string;
}

const NO_CATEGORIES: PhraseLibrary = [];

/**
 * The inner "phrase library" panel: a category dropdown, a cross-category
 * search, and the scrollable list of curated sentences to splice into a text
 * field. A controlled panel with no trigger of its own — the caller owns the
 * open/close — so it drops into the RichTextArea toolbar and its fullscreen
 * editor alike. The library is a prop, so the same component serves any set.
 */
export default function PhrasePickerPanel({
    library,
    onSelect
}: PhrasePickerPanelProps) {
    const intl = useIntl();
    // Normalize: the library may momentarily be undefined (a stale dev bundle),
    // and an empty set should degrade to "no matches", never crash. The
    // fallback is a module constant so the memo below still memoizes.
    const categories = library ?? NO_CATEGORIES;
    const [categoryKey, setCategoryKey] = useState(categories[0]?.key ?? '');
    const [query, setQuery] = useState('');

    // Search spans every category; browsing shows one. A search query wins over
    // the selected category.
    const trimmedQuery = query.trim().toLowerCase();
    const results = useMemo<FlatPhrase[]>(() => {
        if (trimmedQuery) {
            const matches: FlatPhrase[] = [];
            for (const category of categories) {
                for (const phrase of category.phrases) {
                    if (phrase.toLowerCase().includes(trimmedQuery)) {
                        matches.push({
                            phrase,
                            categoryLabel: category.label
                        });
                    }
                }
            }
            return matches;
        }
        const active =
            categories.find((category) => category.key === categoryKey) ??
            categories[0];
        return (active?.phrases ?? []).map((phrase) => ({
            phrase,
            categoryLabel: active?.label ?? ''
        }));
    }, [categories, categoryKey, trimmedQuery]);

    return (
        <Box sx={{ p: 1.5 }}>
            {/* Category picker + search. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <TextField
                    select
                    size="small"
                    label={intl.formatMessage({ id: 'phrases.category' })}
                    value={categoryKey}
                    onChange={(event) => setCategoryKey(event.target.value)}
                    disabled={!!trimmedQuery}
                    sx={{ flex: '1 1 160px' }}
                >
                    {categories.map((category) => (
                        <MenuItem key={category.key} value={category.key}>
                            {category.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={intl.formatMessage({ id: 'phrases.search' })}
                    sx={{ flex: '1 1 160px' }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }
                    }}
                />
            </Box>

            {/* The scrollable phrase list, bounded so the form stays usable. */}
            <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {results.length === 0 ? (
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', px: 1, py: 2 }}
                    >
                        {intl.formatMessage({ id: 'phrases.empty' })}
                    </Typography>
                ) : (
                    <List dense disablePadding>
                        {results.map((entry, index) => (
                            <ListItemButton
                                key={`${entry.phrase}-${index}`}
                                divider
                                onClick={() => onSelect(entry.phrase)}
                            >
                                <ListItemText
                                    primary={entry.phrase}
                                    // Show the source category only while
                                    // searching across all of them.
                                    secondary={
                                        trimmedQuery
                                            ? entry.categoryLabel
                                            : undefined
                                    }
                                    slotProps={{
                                        primary: {
                                            sx: { whiteSpace: 'normal' }
                                        }
                                    }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );
}
